// Service imports:
import { bugsnagNotify, fetchResponseToDetailedString } from '../../services/bugsnagUtils';
// Constant imports:
import { GET_SIGTI_API_URL } from '../../config';
// Type imports:
import { AppDispatch } from '../../index'; // Use the root dispatch type from your store
import {
    ILoginError,
    IResponseLoginSigtiBody
} from '../../types/api/LoginTypes';
import {
    LoginActions,
    LOG_OUT,
    SET_USERNAME_AND_PASSWORD,
    SYSTEM_PROFILE_FAILOVER,
    REQUEST_USER_INFO_SUCCESS,
    REQUEST_USER_INFO_FAILURE,
    START_LOG_OUT_TIMER,
    REQUEST_LOGIN_SUCCESS,
    REQUEST_LOGIN_FAILURE,
    REQUEST_LOGOUT_FAILURE
} from '../../types/redux/LoginTypes';
import { loginQuery, logoutQuery, requestPrivateUserInfoQuery } from '../queries/alertQueries';

// ============================================ LOGIN TO SIGTI ============================================
// Thunk function.
const requestloginThunk = async (dispatch: AppDispatch, username: string, password: string) => {

    dispatch(setUsernameAndPasswordAction(username, password));

    const url = GET_SIGTI_API_URL();
    const query = loginQuery();

    const queryObject = {
        query,
        variables: {
            username: username,
            password: password
          }
    };

    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify(queryObject),
        });

        if (response.ok) {
            const responseBody = await response.json();
            if (!responseBody.data) {
                const errorInfo: ILoginError = {
                    code: 401,
                    reason: 'Session Expired'
                }
                dispatch(requestLoginFailure(errorInfo));
                return;
            }
            dispatch(requestLoginSuccess(responseBody.data.login));
        } else {
            const { errorString, errorBody } = await fetchResponseToDetailedString('Request Access Token', response);
            bugsnagNotify(new Error(errorString));
            const errorInfo: ILoginError = {
                code: response.status,
                reason: errorBody.error
            }
            dispatch(requestLoginFailure(errorInfo));
        }
    } catch (e) {
        bugsnagNotify(e as Error);
        const errorInfo: ILoginError = {
            code: 499,
            reason: 'Forced Ambigious Error'
        }
        dispatch(requestLoginFailure(errorInfo));
    }
}

// Success function.
const requestLoginSuccess = (tokenObject: IResponseLoginSigtiBody): LoginActions => ({
    type: REQUEST_LOGIN_SUCCESS,
    payload: tokenObject
});

// Failure function.
const requestLoginFailure = (errorInfo: ILoginError): LoginActions => ({
    type: REQUEST_LOGIN_FAILURE,
    payload: errorInfo
})

export const requestLogin = (username: string, password: string) => {
    return (dispatch: AppDispatch) => {
        requestloginThunk(dispatch, username, password);
    }
}

// ============================================ LOGOUT OF SIGTI ============================================
const requestlogoutThunk = async (dispatch: AppDispatch, accessToken: string) => {

    const url = GET_SIGTI_API_URL();
    const query = logoutQuery();

    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({ query })
        });

        if (response.ok) {
            dispatch(requestLogoutSuccess());
        } else {
            const { errorString, errorBody } = await fetchResponseToDetailedString('Request Access Token', response);
            bugsnagNotify(new Error(errorString));
            const errorInfo: ILoginError = {
                code: response.status,
                reason: errorBody.error
            }
            dispatch(requestLogoutFailure(errorInfo));
        }
    } catch (e) {
        bugsnagNotify(e as Error);
        const errorInfo: ILoginError = {
            code: 499,
            reason: 'Forced Ambigious Error'
        }
        dispatch(requestLogoutFailure(errorInfo));
    }
}

// Success function.
const requestLogoutSuccess = (): LoginActions => ({
    type: LOG_OUT,
});

// Failure function.
const requestLogoutFailure = (errorInfo: ILoginError): LoginActions => ({
    type: REQUEST_LOGOUT_FAILURE,
    payload: errorInfo
})


/* LOG OUT FUNCTION */
export const logOut = (accessToken: string) => {
    return (dispatch: AppDispatch) => {
        requestlogoutThunk(dispatch, accessToken);
    }
}

const setUsernameAndPasswordAction = (username: string, password: string): LoginActions => ({
    type: SET_USERNAME_AND_PASSWORD,
    payload: {
        username,
        password
    }
})

export const setUsernameAndPassword = (username: string, password: string) => {
    return (dispatch: AppDispatch) => {
        dispatch(setUsernameAndPasswordAction(username, password));
    }
}

// ============================================ REQUEST USER INFO ============================================
const requestUserInfoThunk = async (dispatch: AppDispatch, accessToken: string) => {

    const url = GET_SIGTI_API_URL();
    const query = requestPrivateUserInfoQuery();

    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({ query })
        });

        if (response.ok) {
            const responseBody = await response.json();
            if (!responseBody.data?.privateUserInfo) {
                return;
            }
            dispatch(requestUserInfoSuccess(responseBody.data.privateUserInfo.userName, responseBody.data.privateUserInfo.organization));
        } else {
            const { errorString } = await fetchResponseToDetailedString('Request User Info', response);
            bugsnagNotify(new Error(errorString));
            dispatch(requestUserInfoFailure('Request went wrong'));
        }
    } catch (e) {
        bugsnagNotify(e as Error)
    }
}
const requestUserInfoSuccess = (userName: string, organization: string): LoginActions => ({
    type: REQUEST_USER_INFO_SUCCESS,
    payload: {
        userName,
        organization
    }
})
const requestUserInfoFailure = (error: string): LoginActions => ({
    type: REQUEST_USER_INFO_FAILURE,
    payload: {
        error
    }
})
export const requestUserInfo = (accessToken: string) => {
    return (dispatch: AppDispatch) => {
        requestUserInfoThunk(dispatch, accessToken);
    }
}

/* SYSTEM PROFILE FAILOVER */
export const systemProfileFailover = () => (dispatch: AppDispatch) => {
    dispatch({ type: SYSTEM_PROFILE_FAILOVER });
}

/* START LOG OUT TIMER */
export const startLogOutTimer = () => (dispatch: AppDispatch) => {
    dispatch({ type: START_LOG_OUT_TIMER });
}